/**
 * A few grams of DOM helper, in place of a framework.
 *
 * The desktop client is one hand-written HTML file; this one is hand-written
 * too, and `h()` is the smallest thing that makes that bearable. Text is set
 * through `textContent`, never `innerHTML`, so a caption somebody typed cannot
 * become markup.
 */

type Child = Node | string | number | null | undefined | false;

interface Attrs {
  class?: string;
  text?: string;
  html?: never;
  onclick?: (e: MouseEvent) => void;
  oninput?: (e: Event) => void;
  onchange?: (e: Event) => void;
  onkeydown?: (e: KeyboardEvent) => void;
  [k: string]: unknown;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  apply(el, attrs, children);
  return el;
}

const SVG_NS = "http://www.w3.org/2000/svg";

export function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Attrs | null,
  ...children: Child[]
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  apply(el, attrs, children);
  return el;
}

function apply(el: Element, attrs: Attrs | null | undefined, children: Child[]): void {
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === "text") {
        el.textContent = String(v);
      } else if (k.startsWith("on") && typeof v === "function") {
        el.addEventListener(k.slice(2), v as EventListener);
        wired.add(el);
      } else if (k === "class") {
        el.setAttribute("class", String(v));
      } else if (v === true) {
        el.setAttribute(k, "");
      } else {
        el.setAttribute(k, String(v));
      }
    }
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(typeof c === "object" ? c : document.createTextNode(String(c)));
  }
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function replace(el: Element, ...children: Child[]): void {
  clear(el);
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(typeof c === "object" ? c : document.createTextNode(String(c)));
  }
}

/**
 * Elements given a listener by `h()`.
 *
 * `patch` must not keep one of these whose contents changed: its listener
 * closes over the data it was built from, so the old element would look new
 * and act old.
 */
const wired = new WeakSet<Element>();

/**
 * Elements whose state lives outside their attributes — a playing video, a
 * drawn canvas, an open dialog. Kept when identical, replaced when not, never
 * patched from the inside.
 */
const OPAQUE = new Set(["INPUT", "TEXTAREA", "SELECT", "VIDEO", "AUDIO", "CANVAS", "DIALOG", "IFRAME"]);

/**
 * Bring `parent`'s children in line with `next`, touching only what changed.
 *
 * `replace` is right for a new view and wrong for a live update: it throws
 * away every node, so each image is re-decoded, the scroll position jumps, and
 * a grid of photographs blinks once per message — which during a face pass is
 * once per photograph. This keeps every existing node that is still correct
 * and swaps only the ones that are not, so the one tile that gained a name is
 * the one tile that changes.
 *
 * Children are matched by `data-key` where they have one, and by position and
 * tag where they do not. A matched child that is identical is kept. One that
 * differs is patched from the inside if it is a plain container, and replaced
 * whole if it carries listeners or state of its own, or a `data-sig` saying
 * which version of a record it was drawn from.
 */
export function patch(parent: Element, next: readonly Node[]): void {
  const keyed = new Map<string, Element>();
  const loose: ChildNode[] = [];
  for (const child of Array.from(parent.childNodes)) {
    const key = child instanceof Element ? child.getAttribute("data-key") : null;
    if (key !== null) keyed.set(key, child as Element);
    else loose.push(child);
  }

  let looseAt = 0;
  const final: Node[] = [];
  for (const fresh of next) {
    const key = fresh instanceof Element ? fresh.getAttribute("data-key") : null;
    let old: Node | undefined;
    if (key !== null) {
      old = keyed.get(key);
      keyed.delete(key);
    } else {
      const candidate = loose[looseAt];
      if (candidate && candidate.nodeName === fresh.nodeName) {
        old = candidate;
        looseAt++;
      }
    }
    final.push(old ? reconcile(old, fresh) : fresh);
  }

  // Minimal moves: nodes already in place are stepped over, not re-inserted.
  let cursor: ChildNode | null = parent.firstChild;
  for (const node of final) {
    if (node === cursor) {
      cursor = cursor.nextSibling;
      continue;
    }
    parent.insertBefore(node, cursor);
  }
  while (cursor) {
    const after: ChildNode | null = cursor.nextSibling;
    parent.removeChild(cursor);
    cursor = after;
  }
}

function reconcile(old: Node, fresh: Node): Node {
  if (old.isEqualNode(fresh)) return old;
  if (old.nodeType === Node.TEXT_NODE && fresh.nodeType === Node.TEXT_NODE) {
    old.textContent = fresh.textContent;
    return old;
  }
  if (
    !(old instanceof Element) ||
    !(fresh instanceof Element) ||
    wired.has(old) ||
    wired.has(fresh) ||
    // A signed element stands for a record: when the record changed, every
    // listener inside it may be holding the old one.
    old.hasAttribute("data-sig") ||
    OPAQUE.has(old.nodeName) ||
    old.nodeName !== fresh.nodeName
  ) {
    return fresh;
  }
  for (const attr of Array.from(old.attributes)) {
    if (!fresh.hasAttribute(attr.name)) old.removeAttribute(attr.name);
  }
  for (const attr of Array.from(fresh.attributes)) {
    if (old.getAttribute(attr.name) !== attr.value) old.setAttribute(attr.name, attr.value);
  }
  patch(old, Array.from(fresh.childNodes));
  return old;
}

/**
 * Run a rebuild without losing the reader's place.
 *
 * For the views that cannot be patched — their elements carry observers and
 * listeners attached after `h()` — a live update that did change something
 * still has to redraw. This puts every scrolled container back where it was:
 * the pane, and anything inside it matched by class, which is how a timeline
 * rail keeps its sideways position.
 */
export function keepScroll(root: HTMLElement, rebuild: () => void): void {
  const marks: Array<{ selector: string; top: number; left: number }> = [];
  for (const el of Array.from(root.querySelectorAll<HTMLElement>("*"))) {
    if (!el.scrollTop && !el.scrollLeft) continue;
    const cls = el.getAttribute("class")?.trim().split(/\s+/)[0];
    if (cls) marks.push({ selector: `.${CSS.escape(cls)}`, top: el.scrollTop, left: el.scrollLeft });
  }
  const top = root.scrollTop;
  const left = root.scrollLeft;
  rebuild();
  root.scrollTop = top;
  root.scrollLeft = left;
  for (const m of marks) {
    const el = root.querySelector<HTMLElement>(m.selector);
    if (!el) continue;
    el.scrollTop = m.top;
    el.scrollLeft = m.left;
  }
}

/** Trailing-edge debounce, for the search box and for live-update refreshes. */
export function debounce<A extends unknown[]>(
  ms: number,
  fn: (...args: A) => void,
): (...args: A) => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (t !== undefined) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export const reducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
